import React, { useCallback, useState } from 'react';
import { useNotify, useRefresh, useEditController } from 'react-admin';
import { Edit } from 'ra-ui-materialui';
import { withStyles } from '@material-ui/styles';
import { FormSpy } from 'react-final-form';

import { getInitials, removePathEnd } from '../../core/utils';
import EditToolbar from '../../core/components/edit/EditToolbar';
import FormTitleBar from '../../core/components/FormTitleBar';
import NavigationPrompt from '../../core/components/NavigationPrompt';
import LeaveConfirm from '../../core/components/LeaveConfirmWrapper';
import CustomSimpleForm from '../../core/components/form/CustomSimpleForm';
import { getIcError } from '../../core/providers/utils';
import InvestorTagApi from '../../core/apis/InvestorTagApi';
import InvestorTagsAssociationsApi from '../../core/apis/InvestorTagsAssociationsApi';
import InvestorTagSummary from './InvestorTagSummary';

const styles = () => ({
  noActions: {
    marginTop: '0px'
  }
});

const InvestorTagTitle = ({ record }) => {
  return (
    <FormTitleBar
      showAvatar
      name={record.name}
      avatarContent={getInitials(record.name)}
    />
  );
};

const InvestorTagEdit = props => {
  const displayToolbar = props.match.isExact;
  const [whenBlockNavigation, setWhenBlockNavigation] = useState(false);
  const {
    classes,
    hasEditPermission,
    hasDeletePermission,
    id,
    basePath,
    resource,
    history
  } = props;

  const notify = useNotify();
  const refresh = useRefresh();
  const { record } = useEditController(props);
  const [resetFilter, setResetFilter] = useState(false);

  const onBeforeDelete = () => {
    // Unblock navigation before deleting
    setWhenBlockNavigation(false);
  };

  const handleFormIsDirty = useCallback(
    dirty => {
      // Block navigation when the form is dirty
      setWhenBlockNavigation(dirty);
    },
    [setWhenBlockNavigation]
  );

  const onSave = async values => {
    setResetFilter(false);
    let hasError = false;
    let hasModification = false;
    if (
      record.name !== values.name ||
      record.description !== values.description
    ) {
      hasModification = true;
      const payload = { name: values.name, description: values.description };
      await InvestorTagApi.update(record.id, payload).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'Investor Tag name has not been updated. ' + getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    const investorIdsToAdd = values.investorIds
      .filter(investorId => !record.investorIds.includes(investorId))
      .map(investorId => {
        return { investorTagId: `${record.id}`, investorId: `${investorId}` };
      });
    if (investorIdsToAdd.length > 0) {
      hasModification = true;
      await InvestorTagsAssociationsApi.create(investorIdsToAdd).catch(err => {
        hasError = true;
        notify('ra.notification.server', {
          type: 'warning',
          messageArgs: {
            message:
              'New Investor Tag associations have not been created. ' +
              getIcError(err),
            isForkedWarning: true
          }
        });
      });
    }

    const investorIdsToDelete = record.investorIds
      .filter(investorId => !values.investorIds.includes(investorId))
      .map(investorId => {
        return { investorTagId: `${record.id}`, investorId: `${investorId}` };
      });
    if (investorIdsToDelete.length > 0) {
      hasModification = true;
      await InvestorTagsAssociationsApi.delete(investorIdsToDelete).catch(
        err => {
          hasError = true;
          notify('ra.notification.server', {
            type: 'warning',
            messageArgs: {
              message:
                'Investor Tag associations have not been deleted. ' +
                getIcError(err),
              isForkedWarning: true
            }
          });
        }
      );
    }

    if (!hasError && hasModification) {
      notify('ra.notification.custom_updated_message');
      setResetFilter(true);
      refresh();
    } else {
      setResetFilter(true);
      refresh();
    }
  };

  return (
    <React.Fragment>
      <NavigationPrompt
        hasEditPermission={hasEditPermission}
        adjustMatchingPath={removePathEnd}
        when={whenBlockNavigation}
      >
        {(isOpen, onConfirm, onCancel) => (
          <LeaveConfirm
            isOpen={isOpen}
            onClose={onCancel}
            onConfirm={onConfirm}
          />
        )}
      </NavigationPrompt>
      <Edit
        id={id}
        basePath={basePath}
        resource={resource}
        undoable={false}
        title={<InvestorTagTitle />}
        classes={{ noActions: classes.noActions }}
      >
        <CustomSimpleForm
          variant="standard"
          margin="normal"
          save={onSave}
          toolbar={
            displayToolbar ? (
              <EditToolbar
                history={history}
                hasEditPermission={hasEditPermission}
                hasDeletePermission={hasDeletePermission}
                onBeforeDelete={onBeforeDelete}
                deleteContent="ra.message.confirm_delete_tag"
              ></EditToolbar>
            ) : null
          }
        >
          <FormSpy
            subscription={{ dirty: true }}
            onChange={formStateProps => handleFormIsDirty(formStateProps.dirty)}
          />
          <InvestorTagSummary
            resetFilter={resetFilter}
            hasEditPermission={hasEditPermission}
          />
        </CustomSimpleForm>
      </Edit>
    </React.Fragment>
  );
};

export default withStyles(styles)(InvestorTagEdit);
